/**
 * useAutoInvestigation — Progressive-loading hook for V2 investigation endpoints.
 *
 * Runs 7 investigation analyses SEQUENTIALLY to avoid Neo4j deadlocks.
 * When fired in parallel, concurrent Neo4j transaction saves on the same address
 * cause circular-wait deadlocks (ForsetiClient), leading to cascading timeouts.
 * Sequential execution gives each endpoint exclusive DB access.
 *
 * Pipeline order: fast root-only first, slow expansion last.
 * A prefetch step warms the DB cache before the sequential loop so all
 * root-address fetches are instant cache hits.
 *
 * Each section reports its own status (idle/loading/loaded/error).
 * Follows useDossier.ts patterns: isMounted guard, stale-response cancellation,
 * refresh per-key, and ref-cached results.
 *
 * Sections (ordered fast → slow, signal_assessment LAST to receive all prior data):
 *   exchange_detection | velocity | counterparty_triage |
 *   adaptive_transactions | multi_hop_exposure |
 *   backward_trace | forward_trace | signal_assessment
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { investigationAnalysisService, addressService, type CasePromoteResult } from '../services/api';
import { recordPipeline } from '../utils/pipelineRecorder';
import { normalizeCanvasAddr } from '../utils/canvasSync';
import { useGraphStore } from '../stores/useGraphStore';
import { useWorkbenchSessionStore } from '../stores/useWorkbenchSessionStore';
import { computeLinksMemoVersion } from '../utils/linksSessionMemo';
import {
    useInvestigationResultsStore,
    buildResultsEntry,
    type PersistedResultsEntry,
    type PersistedSection,
} from '../stores/useInvestigationResultsStore';
import { promotionResults, streamCaseLink } from '../components/workbench/caseWallets';
import {
    coverageStepLabel,
    normalizeCoverage,
    plainStepReason,
    stopReasonText,
    stripCoverageStubs,
    type InvestigationCoverage,
} from '../utils/investigationCoverage';

// Map investigation step key -> human-friendly label for toast copy.
const _STEP_LABELS: Record<string, string> = {
    exchange_detection: 'Exchange detection',
    velocity: 'Velocity',
    counterparty_triage: 'Counterparty triage',
    adaptive_transactions: 'Adaptive transactions',
    multi_hop_exposure: 'Multi-hop exposure',
    backward_trace: 'Backward trace',
    forward_trace: 'Forward trace',
    compliant_exchange: 'Compliant exchange',
    signal_assessment: 'Signal assessment',
};

// Ordered fast → slow: DB-only first, cached root next, expansion last
export const INVESTIGATION_KEYS = [
    'exchange_detection',
    'velocity',
    'counterparty_triage',
    'adaptive_transactions',
    'multi_hop_exposure',
    'backward_trace',
    'forward_trace',
    'compliant_exchange',
    'signal_assessment',
] as const;

export type InvestigationKey = typeof INVESTIGATION_KEYS[number];

export interface InvestigationSection {
    status: 'idle' | 'loading' | 'loaded' | 'error';
    health?: 'ok' | 'degraded';
    data?: any;
    error?: string;
    degradedReason?: string;
    /** SSE progress events for streaming sections (backward_trace, forward_trace) */
    progress?: Array<{ type: string; data: any }>;
    /** R-REPORT-03: When this section's data was retrieved */
    loadedAt?: string;
}

export type InvestigationSections = Record<InvestigationKey, InvestigationSection>;

export type PrefetchStatus = 'idle' | 'loading' | 'done' | 'error';

/** One conserved circulation finding (backend cycle_detector output). */
export interface CirculationFinding {
    id: string;
    class: string;            // pass_through | value_shaved_passthrough | self_round_trip | fast_uturn
    token?: string;
    legs?: Array<{ src: string; tgt: string; tx_hash?: string; qty?: number; usd?: number; token?: string; ts?: string }>;
    pivots?: string[];
    origin?: string;
    terminus?: string;
    bottleneck_usd?: number;
    total_dwell_s?: number | null;
    shave_pct?: number;
    confidence?: number;
    interior_exchange?: boolean;
    typology?: string;        // layering_passthrough | circular_flow | rapid_uturn
    involves_subject?: boolean;
}
export interface CirculationResult {
    findings: CirculationFinding[];
    summary?: Record<string, any>;
}

/** Asset-recovery bundle produced by the orchestrator's post-trace passes
 * (seizure matrix, USD reconciliation, flow diagrams, funding nexus, bridge
 * settlements). Arrives in the `complete` event's all_results, NOT as an
 * INVESTIGATION_KEYS section — surfaced separately like `circulation`. */
export interface AssetRecoveryResult {
    seizure_matrix?: any;
    value_reconciliation?: any;
    flow_diagrams?: { executive?: string; detailed?: string } | null;
    funding_sources?: any;
    bridge_settlements?: any[];
    /** Recheck gate verdicts (conservation, per_field_source, ...); printed under Coverage and Limitations. */
    recheck_gates?: any;
}

/** The orchestrator's promotion of a case-linked run, as reported on the `complete` event. */
export type SpinePromotion =
    | ({ status: 'ok' } & Partial<CasePromoteResult>)
    | { status: 'error'; case_id?: string; error?: string };

/** Set when the results on screen were put back from sessionStorage rather than produced by a run in this page visit. */
export interface RestoredFromSession {
    /** Epoch ms the results were written. */
    savedAt: number;
    /** False when the officer left while the run was still going: only the finished steps were kept. */
    complete: boolean;
    /** The canvas gained or lost wallets since the results were written. */
    canvasChanged: boolean;
}

export interface UseAutoInvestigationReturn {
    sections: InvestigationSections;
    /** Non-null while the visible results are a restore of the last run in this tab session. */
    restoredFromSession?: RestoredFromSession | null;
    /** Circulation / pass-through findings (cycle_detector), produced after the
     * backend traces complete. Not an INVESTIGATION_KEYS section. */
    circulation: CirculationResult | null;
    /** Asset-recovery bundle (seizure matrix, reconciliation, flow diagrams). */
    assetRecovery: AssetRecoveryResult | null;
    isRunning: boolean;
    isComplete: boolean;
    prefetchStatus: PrefetchStatus;
    progress: { completed: number; total: number };
    run: (chain: string, address: string, backwardHops?: number, exposureHops?: number) => void;
    /** Unified SSE stream: runs all 7 steps via single EventSource, with live progress + canvas events. */
    runViaStream: (chain: string, address: string, opts?: {
        backwardHops?: number;
        exposureHops?: number;
        forwardHops?: number;
        onLiveGraphEvent?: (event: { type: string; data: any }) => void;
        /** Case the run belongs to (UUID); the backend promotes the completed run to its spine. */
        caseId?: string | null;
        investigationId?: string | null;
    }) => void;
    /** `complete.spine_promotion` of the last streamed run: null when the run carried no case. */
    spinePromotion?: SpinePromotion | null;
    /** Coverage block of the last streamed run (missing and degraded steps with reasons); null before `complete`. */
    coverage?: InvestigationCoverage | null;
    /** The last run's results reduced to the Flow IR inputs for "Promote to case", or null. */
    exportResultsForPromotion?: () => Record<string, any> | null;
    refresh: (chain: string, address: string, key?: InvestigationKey, backwardHops?: number, exposureHops?: number, forwardHops?: number) => void;
    cancel: () => void;
    reset: () => void;
    /** Restore sections from a saved investigation (e.g. loaded from Case/Investigation). */
    hydrate: (
        saved: Partial<Record<InvestigationKey, any>>,
        opts?: { address?: string; circulation?: CirculationResult | null },
    ) => void;
    /** Export current section data for persistence in Investigation graph_state. */
    exportForSave: () => Partial<Record<InvestigationKey, any>> | null;
    /** Address the current investigation results belong to. Empty if no investigation has run. */
    investigatedAddress: string;
}
const INITIAL_SECTIONS: InvestigationSections = {
    exchange_detection: { status: 'idle' },
    velocity: { status: 'idle' },
    counterparty_triage: { status: 'idle' },
    adaptive_transactions: { status: 'idle' },
    multi_hop_exposure: { status: 'idle' },
    backward_trace: { status: 'idle' },
    forward_trace: { status: 'idle' },
    compliant_exchange: { status: 'idle' },
    signal_assessment: { status: 'idle' },
};

export function detectDegraded(data: any): { degraded: boolean; reason?: string } {
    if (!data || typeof data !== 'object') return { degraded: false };

    const diagnostics = data._diagnostics;
    if (diagnostics?.degraded) {
        // Prefer user_hint for end-user-friendly message, fall back to error signatures
        const hint = diagnostics?.user_hint;
        if (hint) {
            return { degraded: true, reason: hint };
        }
        const sigs = diagnostics?.error_signatures;
        if (Array.isArray(sigs) && sigs.length > 0) {
            // Map technical signatures to user-friendly labels
            const friendly = sigs.map((s: string) => {
                if (s === 'upstream_congestion') return 'API rate limits causing delays';
                if (s === 'provider_rate_limit') return 'provider rate-limited';
                if (s === 'provider_timeout') return 'provider timed out';
                if (s === 'etherscan_missing_chainid') return 'chain config error';
                return s;
            });
            return { degraded: true, reason: friendly.join(', ') };
        }
        return { degraded: true, reason: 'upstream provider/config error' };
    }

    // Check data quality flag from multi-hop exposure
    if (data.data_quality === 'partial') {
        const failures = data.sub_analysis_failures;
        return {
            degraded: true,
            reason: failures
                ? `${failures} counterparty analyses failed`
                : 'partial data — some analyses failed',
        };
    }

    // A step that could not run its check (R10): not a clean result.
    if (data.tested === false || data.transactions_unavailable === true) {
        return {
            degraded: true,
            reason: plainStepReason('unavailable', String(data.not_tested_reason || 'transactions unavailable')),
        };
    }

    const status = String(data.status || '').toLowerCase();
    // Step stubs from the stream: timed out or never started. No data, so never "loaded".
    if (status === 'timeout' || status === 'skipped') {
        return { degraded: true, reason: plainStepReason(status, data.reason || data.message || data.error) };
    }
    if (status === 'partial') {
        const stop = data.exploration_summary?.stop_reason;
        if (stop === 'time_budget' || stop === 'limit_reached') {
            const n = data.exploration_summary?.addresses_fetched;
            return {
                degraded: true,
                reason: `Trace stopped early: ${stopReasonText(stop)}${typeof n === 'number' ? ` (${n} addresses explored)` : ''}`,
            };
        }
        if (data.timed_out) {
            return { degraded: true, reason: `Timed out after ${data.timeout_after_s ?? '?'}s: partial result` };
        }
        return { degraded: true, reason: data.message || status };
    }
    if (status === 'degraded') {
        return { degraded: true, reason: data.message || status };
    }

    return { degraded: false };
}

/** Velocity-step conduit result (`conduit_analysis`), computed server-side. */
export interface ConduitAnalysis {
    is_conduit?: boolean;
    pass_through_ratio?: number | null;
    retention_seconds?: number | null;
    primary_token?: string | null;
    confidence?: number | null;
}

const asObject = (v: unknown): Record<string, any> | null =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;

/**
 * Read the velocity step's `conduit_analysis` (gotcha 12: metrics are nested
 * under `data.metrics`, with a top-level fallback). `conduit_metrics` is not
 * read — the backend no longer emits it.
 */
export function extractConduitAnalysis(velocityData: unknown): ConduitAnalysis | null {
    const v = asObject(velocityData);
    if (!v) return null;
    const data = asObject(v.data);
    const candidates = [
        asObject(data?.metrics)?.conduit_analysis,
        asObject(v.metrics)?.conduit_analysis,
        data?.conduit_analysis,
        v.conduit_analysis,
    ];
    for (const c of candidates) {
        const obj = asObject(c);
        if (obj) return obj as ConduitAnalysis;
    }
    return null;
}

/** Read the velocity step's `energy_sponsorship` (same nesting rules). */
export function extractEnergySponsorship(velocityData: unknown): Record<string, any> | null {
    const v = asObject(velocityData);
    if (!v) return null;
    const data = asObject(v.data);
    return asObject(data?.energy_sponsorship)
        ?? asObject(v.energy_sponsorship)
        ?? asObject(asObject(data?.metrics)?.energy_sponsorship)
        ?? asObject(asObject(v.metrics)?.energy_sponsorship);
}

/**
 * Display-only behavioural facts from the velocity / circulation steps. These
 * never change the risk score: scoring belongs to the backend signal
 * assembler (R10/R21), so the frontend adds no risk for energy delegation,
 * pass-through or loop candidates.
 */
export function behaviouralFactsFromVelocity(velocityData: unknown, circulationData?: unknown): {
    flags: string[];
    energy: Record<string, any> | null;
    conduit: ConduitAnalysis | null;
    loops: any[] | null;
} {
    const flags: string[] = [];
    const energy = extractEnergySponsorship(velocityData);
    if (energy) {
        // R10: triggered === null = the check could not run (NOT TESTED); it is
        // reported as such, never as clear and never as a hit.
        if (energy.triggered === null || energy.tested === false) {
            flags.push('energy_delegation_not_tested');
        } else {
            const count = Number(energy.delegator_count ?? (Array.isArray(energy.delegators) ? energy.delegators.length : 0)) || 0;
            const sun = Number(energy.delegated_energy_sun) || 0;
            const observed = typeof energy.delegation_observed === 'boolean' ? energy.delegation_observed : (count > 0 || sun > 0);
            if (energy.triggered === true) flags.push(`energy_delegation_corroborated:${count}`);
            else if (observed) flags.push(`energy_delegation_observed:${count}`);
        }
    }
    const conduit = extractConduitAnalysis(velocityData);
    if (conduit?.is_conduit) flags.push('pass_through_conduit_observed');
    const circ = asObject(circulationData);
    const loopsRaw = asObject(circ?.data)?.hawala_loops ?? circ?.hawala_loops;
    const loops = Array.isArray(loopsRaw) && loopsRaw.length > 0 ? loopsRaw : null;
    if (loops) flags.push(`closed_loop_candidate:${loops.length}`);
    return { flags, energy, conduit, loops };
}

/** The case the Workbench session is in (set when a Workbench opens from a case), or null. */
function workbenchCaseId(): string | null {
    try {
        return useWorkbenchSessionStore.getState().caseId ?? null;
    } catch {
        return null;
    }
}

function buildFetchers(
    chain: string,
    address: string,
    backwardHops: number = 4,
    exposureHops: number = 3,
    forwardHops: number = 4,
    priorResults?: Partial<Record<InvestigationKey, any>> & Record<string, any>,
    onSectionProgress?: (key: InvestigationKey, event: { type: string; data: any }) => void,
    activeCloseRef?: { current: (() => void) | null },
    /** Case the Workbench is in (UUID); labels the explain call's AI audit row so the case report discloses it. */
    explainCaseId?: string | null,
): Record<InvestigationKey, () => Promise<any>> {
    return {
        exchange_detection: () => investigationAnalysisService.detectExchange(chain, address),
        velocity: () => investigationAnalysisService.velocity(chain, address),
        counterparty_triage: () => investigationAnalysisService.counterpartyTriage(chain, address),
        adaptive_transactions: () => investigationAnalysisService.adaptiveTransactions(chain, address),
        signal_assessment: async () => {
            // Enrich the AI explanation with data from prior investigation sections
            const prior = priorResults || {};
            const velocityData = prior.velocity;
            const counterpartyData = prior.counterparty_triage;
            const exchangeData = prior.exchange_detection;
            const exposureData = prior.multi_hop_exposure;

            const behavioralFlags: string[] = [];
            let riskScore = 0;

            // Extract velocity signals
            if (velocityData) {
                const profile = velocityData.velocity_profile || velocityData.data?.velocity_profile;
                if (profile && profile !== 'Mixed') behavioralFlags.push(`velocity_profile:${profile}`);
                const vrs = velocityData.velocity_risk_score ?? velocityData.data?.velocity_risk_score;
                if (typeof vrs === 'number') riskScore = Math.max(riskScore, vrs);
                const metrics = velocityData.data?.metrics || velocityData.metrics;
                if (metrics) {
                    const ftr = metrics.fund_through_rate;
                    if (typeof ftr === 'number' && ftr > 0.9) behavioralFlags.push('high_fund_through_rate');
                    const dwell = metrics.dwell_time_hours;
                    if (typeof dwell === 'number' && dwell < 1) behavioralFlags.push('low_dwell_time');
                }
            }

            // Extract exchange detection signals
            if (exchangeData) {
                if (exchangeData.is_exchange) behavioralFlags.push(`exchange:${exchangeData.exchange_name || exchangeData.exchange_type || 'detected'}`);
            }

            // Extract counterparty signals
            if (counterpartyData?.counterparties) {
                const sanctioned = counterpartyData.counterparties.filter((c: any) => c.sanctioned);
                if (sanctioned.length > 0) behavioralFlags.push(`sanctioned_counterparties:${sanctioned.length}`);
                const severe = counterpartyData.counterparties.filter((c: any) => c.risk_tier === 'severe');
                if (severe.length > 0) behavioralFlags.push(`severe_risk_counterparties:${severe.length}`);
            }

            // Extract exposure signals
            if (exposureData) {
                const combinedRisk = exposureData.combined_risk_score;
                if (typeof combinedRisk === 'number') riskScore = Math.max(riskScore, combinedRisk);
            }

            // Build graph summary from prior investigation results so the AI
            // prompt receives real metrics instead of empty topology data.
            const summary: Record<string, any> = {};

            if (counterpartyData?.counterparties) {
                const cpCount = counterpartyData.counterparties.length;
                summary.unique_counterparties = cpCount;
                // Approximate graph topology: root + counterparties = node count,
                // each counterparty has at least 1 edge = edge count
                summary.node_count = cpCount + 1;
                summary.edge_count = counterpartyData.total_transactions ?? cpCount;
                const mapCp = (c: any) => ({
                    address: c.address,
                    entity: c.entity_name || c.entity_type || null,
                    volume: c.primary_volume ?? c.total_volume ?? c.volume ?? null,
                    token_symbol: c.primary_token || c.token_symbol || c.token || null,
                    risk_tier: c.risk_tier ?? null,
                    // true / false / null (null = screening did not run, NOT TESTED)
                    sanctioned: c.sanctioned === true ? true : c.sanctioned === false ? false : null,
                });
                const top5 = counterpartyData.counterparties.slice(0, 5).map(mapCp);
                const top5Addrs = new Set(top5.map((c: any) => c.address));
                // Ensure ALL high-risk/sanctioned counterparties are visible to the LLM,
                // even if they rank below top-5 by volume (e.g. ChangeNOW with tiny volume).
                const highRiskExtras = counterpartyData.counterparties
                    .filter((c: any) => {
                        if (top5Addrs.has(c.address)) return false;
                        const tier = String(c.risk_tier || '').toLowerCase();
                        return tier === 'high' || tier === 'severe' || tier === 'critical' || !!c.sanctioned;
                    })
                    .map(mapCp);
                summary.top_counterparties = [...top5, ...highRiskExtras];
                summary.counterparty_stats = {
                    total: cpCount,
                    sanctioned_count: counterpartyData.counterparties.filter((c: any) => !!c.sanctioned).length,
                    high_risk_count: counterpartyData.counterparties.filter((c: any) => {
                        const tier = String(c.risk_tier || '').toLowerCase();
                        return tier === 'high' || tier === 'severe' || tier === 'critical';
                    }).length,
                };
                const totalVol = counterpartyData.counterparties.reduce(
                    (s: number, c: any) => s + (Number(c.primary_volume ?? c.total_volume ?? c.volume) || 0), 0,
                );
                if (totalVol > 0) summary.total_value = totalVol;

                // Token-level evidence for Indian Nexus and chain-specific narratives.
                const tokenVolumes: Record<string, number> = {};
                for (const c of counterpartyData.counterparties as any[]) {
                    const token = String(c.primary_token || c.token_symbol || c.token || '').toUpperCase();
                    const vol = Number(c.primary_volume ?? c.total_volume ?? c.volume) || 0;
                    if (!token || vol <= 0) continue;
                    tokenVolumes[token] = (tokenVolumes[token] || 0) + vol;
                }
                if (Object.keys(tokenVolumes).length > 0) {
                    summary.token_volumes = tokenVolumes;
                }
            }

            if (velocityData) {
                const metrics = velocityData.data?.metrics || velocityData.metrics;
                if (metrics) summary.velocity_metrics = metrics;
                const profile = velocityData.velocity_profile || velocityData.data?.velocity_profile;
                if (profile) summary.velocity_profile = profile;
            }

            // Display-only behavioural facts (energy delegation, pass-through,
            // loop candidates). No client-side risk arithmetic: the backend
            // signal assembler owns scoring (R10/R21).
            const facts = behaviouralFactsFromVelocity(velocityData, prior.circulation);
            for (const f of facts.flags) {
                if (!behavioralFlags.includes(f)) behavioralFlags.push(f);
            }
            if (facts.energy) summary.energy_sponsorship = facts.energy;
            if (facts.conduit) summary.conduit_analysis = facts.conduit;
            if (facts.loops) summary.hawala_loops = facts.loops;

            if (exchangeData) {
                summary.is_exchange = exchangeData.is_exchange || false;
                if (exchangeData.exchange_name) summary.exchange_name = exchangeData.exchange_name;
            }

            if (exposureData) {
                summary.exposure_risk = exposureData.combined_risk_score ?? null;
                if (exposureData.source_risk) summary.source_risk = exposureData.source_risk;
                if (exposureData.destination_risk) summary.destination_risk = exposureData.destination_risk;
            }

            // Extract backward/forward trace signals (available because signal_assessment runs last)
            const backwardData = prior.backward_trace;
            const forwardData = prior.forward_trace;

            // Data quality metadata — tells the AI service and signal assembler
            // whether prior pipeline sections loaded or failed, so they can
            // distinguish "API failure → no data" from "genuinely clean address".
            summary.data_quality = {
                velocity: prior.velocity ? 'loaded' : 'unavailable',
                counterparty_triage: prior.counterparty_triage ? 'loaded' : 'unavailable',
                adaptive_transactions: prior.adaptive_transactions ? 'loaded' : 'unavailable',
                multi_hop_exposure: prior.multi_hop_exposure ? 'loaded' : 'unavailable',
                backward_trace: backwardData ? 'loaded' : 'unavailable',
                forward_trace: forwardData ? 'loaded' : 'unavailable',
            };

            if (backwardData) {
                const paths = backwardData.paths || backwardData.data?.paths || [];
                if (Array.isArray(paths) && paths.length > 0) {
                    for (const path of paths) {
                        const entity = path.entity_name || path.entity;
                        const entityType = (path.entity_type || path.type || '').toLowerCase();
                        if (entity) {
                            behavioralFlags.push(`source_entity:${entity}`);
                            if (entityType === 'scam' || entityType === 'mixer' ||
                                entity.toLowerCase().includes('chainabuse') ||
                                entity.toLowerCase().includes('mixer') ||
                                entity.toLowerCase().includes('tornado')) {
                                behavioralFlags.push(`high_risk_source:${entity}`);
                            }
                        }
                    }
                    summary.backward_trace_paths = paths.length;
                }
                summary.backward_trace_entities = backwardData.entities_found ?? paths.length ?? 0;
            }

            if (forwardData) {
                const paths = forwardData.paths || forwardData.data?.paths || [];
                if (Array.isArray(paths) && paths.length > 0) {
                    for (const path of paths) {
                        const entity = path.entity_name || path.entity;
                        const entityType = (path.entity_type || path.type || '').toLowerCase();
                        if (entity) {
                            behavioralFlags.push(`destination_entity:${entity}`);
                            if (entityType === 'scam' || entityType === 'mixer' ||
                                entity.toLowerCase().includes('chainabuse') ||
                                entity.toLowerCase().includes('mixer') ||
                                entity.toLowerCase().includes('tornado') ||
                                entity.toLowerCase().includes('phish') ||
                                entity.toLowerCase().includes('hack')) {
                                behavioralFlags.push(`scam_destination:${entity}`);
                            }
                        }
                    }
                    summary.forward_trace_paths = paths.length;
                }
                summary.forward_trace_entities = forwardData.entities_found ?? paths.length ?? 0;
            }

            // V3-H2/H3: Use orchestrator reconciliation data — unified entity
            // list from ALL steps (counterparty, multi-hop, backward/forward
            // trace).  This ensures the LLM sees trace-discovered entities
            // like ChainAbuse scam even when they aren't in hop-1 counterparties.
            const reconciliation = prior._reconciliation;
            if (reconciliation) {
                // Add trace-discovered high-risk entities to behavioral flags
                const hrEntities = reconciliation.high_risk_trace_entities || [];
                for (const ent of hrEntities) {
                    const flag = `trace_high_risk:${ent.name} (${ent.type}, hop ${ent.hop}, via ${ent.source_step})`;
                    if (!behavioralFlags.includes(flag)) {
                        behavioralFlags.push(flag);
                    }
                }

                // Add unified trace entities to summary for the LLM prompt
                summary.trace_entities = (reconciliation.trace_entities || []).map((e: any) => ({
                    name: e.name,
                    type: e.type,
                    hop: e.hop,
                    source_step: e.source_step,
                    address: e.address,
                }));
                summary.trace_entity_stats = {
                    total: reconciliation.total_entities_discovered ?? 0,
                    high_risk_count: hrEntities.length,
                    high_risk_names: hrEntities.map((e: any) => e.name),
                };

                // V3-H3: Boost risk score from trace discoveries
                const boost = reconciliation.risk_boost ?? 0;
                if (boost > 0) {
                    riskScore = Math.min(riskScore + boost, 1.0);
                    behavioralFlags.push(`risk_boosted_by_trace:+${(boost * 100).toFixed(0)}%`);
                }
            }

            // Add transaction-level evidence when adaptive transaction data is available.
            const adaptiveData = prior.adaptive_transactions;
            const txRows: any[] = adaptiveData?.transactions || adaptiveData?.data?.transactions || [];
            if (Array.isArray(txRows) && txRows.length > 0) {
                const tokenTypeCounts: Record<string, number> = {};
                let usdtTrc20Count = 0;
                for (const tx of txRows) {
                    const sym = String(tx.token_symbol || tx.token || '').toUpperCase();
                    const typ = String(tx.tx_type || tx.type || '').toUpperCase();
                    const key = [sym || 'UNKNOWN', typ || 'UNKNOWN'].join(':');
                    tokenTypeCounts[key] = (tokenTypeCounts[key] || 0) + 1;
                    if (sym === 'USDT' && typ.includes('TRC20')) usdtTrc20Count += 1;
                }
                summary.transaction_evidence = {
                    total_transactions: txRows.length,
                    usdt_trc20_count: usdtTrc20Count,
                    token_type_counts: tokenTypeCounts,
                };
            }

            const explainCase = streamCaseLink(explainCaseId, null).caseId;
            const explainResult = await addressService.explainInvestigation(
                { nodes: [{ id: address }], edges: [], chain, summary },
                { behavioral_flags: behavioralFlags, risk_score: riskScore, overall_risk_score: riskScore },
                chain,
                ...(explainCase ? [{ caseId: explainCase }] : []),
            );

            // Attach investigation-derived risk score so the LE report can
            // prefer it over the triage default (which is a static 10% for
            // unknown addresses).  Also include the tx count so the report
            // can show "INSUFFICIENT DATA" when the investigation had zero
            // transactions to analyze.
            // No fallback to 1 when counterparties loaded: zero analysed
            // transactions must reach the report as "insufficient data".
            const investigationTxCount = countAnalysedTransactions(
                summary.transaction_evidence?.total_transactions, velocityData,
            );
            return {
                ...explainResult,
                investigation_risk_score: riskScore,
                investigation_tx_count: investigationTxCount,
                investigation_behavioral_flags: behavioralFlags,
                // V3-H4: Provider health for report methodology section
                provider_health: prior._provider_health || null,
                // V3-H2/H3: Reconciliation data for report
                reconciliation: reconciliation || null,
                // Transaction summary across all fetch mechanisms
                transaction_summary: prior._transaction_summary || null,
                // Coverage contract: missing and degraded steps with reasons
                coverage: prior._coverage || null,
            };
        },
        multi_hop_exposure: () => investigationAnalysisService.multiHopExposure(chain, address, exposureHops),
        backward_trace: () => {
            const { promise, close } = investigationAnalysisService.backwardTraceStream(
                chain, address, backwardHops,
                (event) => onSectionProgress?.('backward_trace', event),
            );
            if (activeCloseRef) activeCloseRef.current = close;
            return promise;
        },
        forward_trace: () => {
            const { promise, close } = investigationAnalysisService.forwardTraceStream(
                chain, address, forwardHops,
                (event) => onSectionProgress?.('forward_trace', event),
            );
            if (activeCloseRef) activeCloseRef.current = close;
            return promise;
        },
        compliant_exchange: () => {
            // `findCompliantExchangeStream` returns only a close handler and a
            // 2-arg (evtType, data) callback — different shape from the other
            // stream endpoints. Wrap it here so the run loop still sees the
            // {promise, close} + {type, data} contract it depends on.
            let resolvePromise!: (value: any) => void;
            let finalPayload: any = null;
            const promise = new Promise<any>((resolve) => { resolvePromise = resolve; });
            const close = addressService.findCompliantExchangeStream(
                chain, address,
                (evtType, data) => {
                    onSectionProgress?.('compliant_exchange', { type: evtType, data });
                    if (evtType === 'complete') {
                        finalPayload = data;
                        resolvePromise(data);
                    } else if (evtType === 'error') {
                        resolvePromise(finalPayload ?? { error: data?.error || 'compliant_exchange stream error' });
                    }
                },
            );
            if (activeCloseRef) activeCloseRef.current = close;
            return promise;
        },
    };
}

/**
 * Number of transactions the investigation actually analysed: the larger of the
 * adaptive-transactions row count and the velocity analyser's own count.
 * Returns 0 (never an assumed 1) when neither step produced a count.
 */
export function countAnalysedTransactions(adaptiveCount: unknown, velocityData: any): number {
    const nums = [
        adaptiveCount,
        velocityData?.transaction_count,
        velocityData?.data?.transaction_count,
        velocityData?.metrics?.total_txs,
        velocityData?.data?.metrics?.total_txs,
    ].map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
    return nums.length ? Math.max(...nums) : 0;
}

function canvasAddressNodeIds(elements: any[]): string[] {
    return elements
        .filter((el) => !el?.data?.source && el?.data?.type === 'address' && el?.data?.id)
        .map((el) => String(el.data.id));
}

export interface RestoredResults {
    sections: InvestigationSections;
    circulation: CirculationResult | null;
    assetRecovery: AssetRecoveryResult | null;
    coverage: InvestigationCoverage | null;
    spinePromotion: SpinePromotion | null;
    address: string;
    chain: string;
    meta: RestoredFromSession;
}

/**
 * Put the persisted results of the last run back, but only while the canvas
 * still holds the root node they belong to (a different or emptied canvas
 * means they describe something that is no longer on screen).
 */
export function readRestoredResults(
    entry: PersistedResultsEntry | null = useInvestigationResultsStore.getState().entry,
    elements: any[] = useGraphStore.getState().elements,
): RestoredResults | null {
    if (!entry || !entry.address) return null;
    const rootOnCanvas = elements.some((el) => !el?.data?.source
        && (el?.data?.id === entry.address || el?.data?.id === normalizeCanvasAddr(entry.address)));
    if (!rootOnCanvas) return null;
    const sections = { ...INITIAL_SECTIONS } as InvestigationSections;
    let any = false;
    for (const key of INVESTIGATION_KEYS) {
        const saved = entry.sections?.[key];
        if (!saved) continue;
        sections[key] = {
            status: 'loaded',
            data: saved.data,
            loadedAt: saved.loadedAt,
            health: saved.health as InvestigationSection['health'],
            degradedReason: saved.degradedReason,
        };
        any = true;
    }
    if (!any) return null;
    return {
        sections,
        circulation: (entry.circulation as CirculationResult | null) ?? null,
        assetRecovery: (entry.assetRecovery as AssetRecoveryResult | null) ?? null,
        coverage: (entry.coverage as InvestigationCoverage | null) ?? null,
        spinePromotion: (entry.spinePromotion as SpinePromotion | null) ?? null,
        address: entry.address,
        chain: entry.chain,
        meta: {
            savedAt: entry.savedAt,
            complete: entry.complete,
            canvasChanged: computeLinksMemoVersion(canvasAddressNodeIds(elements)) !== entry.canvasVersion,
        },
    };
}

export function useAutoInvestigation(): UseAutoInvestigationReturn {
    // Results of the last run in this tab session (sessionStorage), read once per mount.
    const restoredRef = useRef<RestoredResults | null | undefined>(undefined);
    if (restoredRef.current === undefined) restoredRef.current = readRestoredResults();
    const restored = restoredRef.current;
    const [sections, setSections] = useState<InvestigationSections>(() => restored?.sections ?? { ...INITIAL_SECTIONS });
    const [circulation, setCirculation] = useState<CirculationResult | null>(restored?.circulation ?? null);
    const [assetRecovery, setAssetRecovery] = useState<AssetRecoveryResult | null>(restored?.assetRecovery ?? null);
    const [isRunning, setIsRunning] = useState(false);
    const [isComplete, setIsComplete] = useState(!!restored);
    const [prefetchStatus, setPrefetchStatus] = useState<PrefetchStatus>('idle');
    const [spinePromotion, setSpinePromotion] = useState<SpinePromotion | null>(restored?.spinePromotion ?? null);
    const [coverage, setCoverage] = useState<InvestigationCoverage | null>(restored?.coverage ?? null);
    const [restoredFromSession, setRestoredFromSession] = useState<RestoredFromSession | null>(restored?.meta ?? null);
    // Raw `complete.all_results` of the last streamed run (the Flow IR inputs live there).
    const lastStreamResultsRef = useRef<Record<string, any> | null>(null);
    const cancelledRef = useRef(false);
    const runIdRef = useRef(0);
    const activeCloseRef = useRef<(() => void) | null>(null);
    const malformedSectionKeysRef = useRef<Set<InvestigationKey>>(new Set());

    // Track which address the current results belong to.
    // When the AutoInvestigateCard's address changes, it compares to this
    // to decide whether to show cached results or a fresh "Run" button.
    const investigatedAddressRef = useRef<string>(restored?.address ?? '');
    const investigatedChainRef = useRef<string>(restored?.chain ?? '');

    const getSectionSafe = useCallback((snapshot: Partial<InvestigationSections>, key: InvestigationKey): InvestigationSection => {
        const section = snapshot[key];
        if (section) return section;

        if (!malformedSectionKeysRef.current.has(key)) {
            malformedSectionKeysRef.current.add(key);
            console.error('[useAutoInvestigation] Missing section key in investigation state.', {
                missingKey: key,
                availableKeys: Object.keys(snapshot || {}),
            });
            Sentry.captureMessage('[useAutoInvestigation] Missing section key in investigation state', {
                level: 'warning',
                extra: {
                    missingKey: key,
                    availableKeys: Object.keys(snapshot || {}),
                },
            });
        }

        return {
            status: 'error',
            error: `Internal state mismatch: missing section "${key}"`,
        };
    }, []);

    const markSection = useCallback((key: InvestigationKey, update: InvestigationSection) => {
        setSections(prev => ({ ...prev, [key]: update }));
    }, []);

    const getProgress = useCallback((): { completed: number; total: number } => {
        let completed = 0;
        const snap = sections;
        for (const key of INVESTIGATION_KEYS) {
            const s = getSectionSafe(snap, key).status;
            if (s === 'loaded' || s === 'error') completed++;
        }
        return { completed, total: INVESTIGATION_KEYS.length };
    }, [sections, getSectionSafe]);

    const runKeys = useCallback(async (
        chain: string,
        address: string,
        keys: readonly InvestigationKey[],
        runId: number,
        backwardHops: number = 4,
        exposureHops: number = 3,
        forwardHops: number = 4,
    ) => {
        // Pre-warm DB cache with root address transactions before sequential loop.
        // This ensures all root-only endpoints (velocity, counterparty, exchange, signal)
        // get instant cache hits instead of each making a live API call.
        setPrefetchStatus('loading');
        try {
            const prefetchResult = await investigationAnalysisService.prefetchRoot(chain, address);
            const prefetchHealth = detectDegraded(prefetchResult);
            if (prefetchHealth.degraded || prefetchResult?.config_error) {
                // Upstream config error (missing chainid, invalid API key, etc.)
                // Abort the entire pipeline — all subsequent steps would fail with
                // the same error, wasting time and hammering the API.
                setPrefetchStatus('error');
                const errorMsg = prefetchResult?.config_error
                    || prefetchResult?.message
                    || prefetchHealth.reason
                    || 'Upstream API configuration error — check server logs';
                for (const key of keys) {
                    markSection(key, { status: 'error', error: errorMsg });
                }
                return;
            }
            setPrefetchStatus('done');
        } catch {
            // Non-fatal: endpoints will self-fetch on cache miss
            setPrefetchStatus('error');
        }

        if (cancelledRef.current || runIdRef.current !== runId) return;

        // Collect results from completed sections so signal_assessment can use them
        const collectedResults: Partial<Record<InvestigationKey, any>> & Record<string, any> = {};

        // SSE progress callback: appends events to section progress array
        const onSectionProgress = (key: InvestigationKey, event: { type: string; data: any }) => {
            setSections(prev => ({
                ...prev,
                [key]: {
                    ...prev[key],
                    progress: [...(prev[key].progress || []), event],
                },
            }));
        };

        // Run sequentially to avoid Neo4j deadlocks from concurrent graph writes.
        // Each section is marked loading → loaded/error before the next starts,
        // giving the UI real-time progress updates.
        for (const key of keys) {
            if (cancelledRef.current || runIdRef.current !== runId) return;

            markSection(key, { status: 'loading' });
            activeCloseRef.current = null;

            // Rebuild fetchers each iteration so signal_assessment gets prior results
            const fetchers = buildFetchers(chain, address, backwardHops, exposureHops, forwardHops, collectedResults, onSectionProgress, activeCloseRef,
                workbenchCaseId());

            try {
                const data = await fetchers[key]();
                if (cancelledRef.current || runIdRef.current !== runId) return;
                const degraded = detectDegraded(data);
                markSection(key, {
                    status: 'loaded',
                    health: degraded.degraded ? 'degraded' : 'ok',
                    degradedReason: degraded.reason,
                    data,
                    loadedAt: new Date().toISOString(),
                });
                collectedResults[key] = data;
            } catch (err: any) {
                if (cancelledRef.current || runIdRef.current !== runId) return;
                markSection(key, {
                    status: 'error',
                    error: err?.message || 'Request failed',
                });
            }
            activeCloseRef.current = null;
        }
    }, [markSection]);

    const run = useCallback((chain: string, address: string, backwardHops: number = 4, exposureHops: number = 3, forwardHops: number = 4) => {
        cancelledRef.current = false;
        const id = ++runIdRef.current;
        setRestoredFromSession(null);
        useInvestigationResultsStore.getState().clear();
        setIsRunning(true);
        setIsComplete(false);
        setPrefetchStatus('idle');
        setSections({ ...INITIAL_SECTIONS });
        setCirculation(null);
        setAssetRecovery(null);
        setSpinePromotion(null);
        lastStreamResultsRef.current = null;

        runKeys(chain, address, INVESTIGATION_KEYS, id, backwardHops, exposureHops, forwardHops).finally(() => {
            if (runIdRef.current === id) {
                setIsRunning(false);
                setIsComplete(true);
            }
        });
    }, [runKeys]);

    /**
     * Unified SSE stream — runs entire investigation via single EventSource.
     *
     * Backend orchestrates all 7 steps sequentially and streams typed events.
     * Signal assessment (step 8) still runs on the frontend after the
     * 'complete' event arrives with all prior results.
     *
     * Falls back to legacy run() if EventSource connection fails immediately.
     */
    const runViaStream = useCallback((
        chain: string,
        address: string,
        opts?: {
            backwardHops?: number;
            exposureHops?: number;
            forwardHops?: number;
            onLiveGraphEvent?: (event: { type: string; data: any }) => void;
            caseId?: string | null;
            investigationId?: string | null;
        },
    ) => {
        const backwardHops = opts?.backwardHops ?? 4;
        const exposureHops = opts?.exposureHops ?? 3;
        const forwardHops = opts?.forwardHops ?? 4;
        const onLiveGraphEvent = opts?.onLiveGraphEvent;
        const caseLink = streamCaseLink(opts?.caseId, opts?.investigationId);

        cancelledRef.current = false;
        const id = ++runIdRef.current;
        investigatedAddressRef.current = address; // Track which address these results belong to
        investigatedChainRef.current = chain;
        // A new run replaces the restored results, in memory and in sessionStorage.
        setRestoredFromSession(null);
        useInvestigationResultsStore.getState().clear();
        setIsRunning(true);
        setIsComplete(false);
        setPrefetchStatus('loading'); // Backend handles prefetch first
        setSections({ ...INITIAL_SECTIONS });
        setCirculation(null);
        setAssetRecovery(null);
        setSpinePromotion(null);
        setCoverage(null);
        lastStreamResultsRef.current = null;

        // Accumulated results so signal_assessment can use them
        const collectedResults: Partial<Record<InvestigationKey, any>> & Record<string, any> = {};
        // Track current step for events that may lack a "step" field (phase/hop)
        let currentStep: InvestigationKey | null = null;
        // Track degraded/failed steps so we can surface a single summary toast
        // at `complete` rather than spamming the user with one per section.
        // Map: step key -> short reason (or "(no detail)" / "failed").
        const degradedSteps = new Map<string, string>();
        const failedSteps = new Map<string, string>();
        // Steps that timed out or never ran: no data at all, so they get their own toast.
        const unfinishedSteps = new Map<string, string>();
        // B1 defense: once the orchestrator emits 'complete', drop any further
        // graph-mutating events. The backend SHOULD stop emitting after
        // complete (orchestrator.emit() is a no-op when cancelled), but
        // fire-and-forget tasks spawned by individual analyzers can still
        // enqueue late events. The Sentry counter lets us see whether late
        // events actually fire in production so we can chase down the source.
        let streamCompleted = false;
        let lateEventCount = 0;
        const POST_COMPLETE_DROP_TYPES = new Set([
            'node_discovered', 'edge_discovered', 'decision',
            'progress', 'phase', 'hop', 'step_start', 'step_result',
        ]);

        const onEvent = (event: { type: string; data: any }) => {
            // Bail if cancelled or superseded by a newer run
            if (cancelledRef.current || runIdRef.current !== id) return;

            // B1 cutoff: drop side-effecting events after 'complete'.
            // 'heartbeat' and 'connection_error' are still allowed through —
            // they don't mutate canvas state.
            if (streamCompleted && POST_COMPLETE_DROP_TYPES.has(event.type)) {
                lateEventCount += 1;
                if (lateEventCount === 1) {
                    // Only log the first one per stream so we don't spam Sentry.
                    Sentry.captureMessage('[useAutoInvestigation] late SSE event after complete', {
                        level: 'warning',
                        extra: {
                            event_type: event.type,
                            step: event.data?.step,
                            chain, address,
                        },
                    });
                    console.warn('[useAutoInvestigation] dropped late event', event.type, event.data?.step);
                }
                return;
            }

            switch (event.type) {
                case 'step_start': {
                    const step = event.data.step as InvestigationKey;
                    currentStep = INVESTIGATION_KEYS.includes(step) ? step : null;
                    if (!currentStep) break; // Unknown step — ignore
                    // Prefetch is done once the first real step starts
                    setPrefetchStatus('done');
                    markSection(currentStep, { status: 'loading' });
                    break;
                }

                case 'step_result': {
                    const step = event.data.step as InvestigationKey;
                    if (!INVESTIGATION_KEYS.includes(step)) break; // Unknown step — ignore
                    const data = event.data.data;
                    const degraded = detectDegraded(data);
                    if (event.data.status === 'error') {
                        markSection(step, {
                            status: 'error',
                            error: event.data.error || 'Step failed',
                        });
                        failedSteps.set(step, event.data.error || 'failed');
                        recordPipeline('sse', `step_result.${step}`, { status: 'loaded' }, { status: 'error', error: event.data.error }, `step=${step} FAILED`);
                    } else if (event.data.status === 'timeout' || event.data.status === 'skipped') {
                        // No data came back. Not loaded: failed-and-degraded with a plain reason.
                        const why = detectDegraded(event.data).reason
                            || plainStepReason(event.data.status, event.data.reason);
                        markSection(step, {
                            status: 'error',
                            error: why,
                            health: 'degraded',
                            degradedReason: why,
                        });
                        unfinishedSteps.set(step, why);
                        recordPipeline('sse', `step_result.${step}`, { status: 'loaded' }, { status: event.data.status, reason: why }, `step=${step} ${String(event.data.status).toUpperCase()}`);
                    } else {
                        markSection(step, {
                            status: 'loaded',
                            health: degraded.degraded ? 'degraded' : 'ok',
                            degradedReason: degraded.reason,
                            data,
                            loadedAt: new Date().toISOString(),
                        });
                        if (degraded.degraded) {
                            degradedSteps.set(step, degraded.reason || 'partial data');
                        }
                        collectedResults[step] = data;
                        recordPipeline('sse', `step_result.${step}`, { status: 'loaded' }, {
                            status: 'loaded',
                            has_data: !!data,
                            degraded: degraded.degraded,
                            nodes: data?.nodes?.length,
                            edges: data?.edges?.length,
                            paths: data?.paths?.length,
                        }, `step=${step} duration=${event.data.duration_s || '?'}s`);
                    }
                    break;
                }

                case 'progress': {
                    // Use currentStep as fallback when event.data.step
                    // doesn't match an investigation key (e.g. BFS emits
                    // "incoming_trace" but the active step is "backward_trace")
                    const rawStep = event.data.step as string | undefined;
                    const step = (rawStep && INVESTIGATION_KEYS.includes(rawStep as InvestigationKey)
                        ? rawStep
                        : currentStep) as InvestigationKey | undefined;
                    if (!step || !INVESTIGATION_KEYS.includes(step)) break;
                    setSections(prev => ({
                        ...prev,
                        [step]: {
                            ...prev[step],
                            progress: [...(prev[step]?.progress || []), event],
                        },
                    }));
                    break;
                }

                case 'node_discovered':
                case 'edge_discovered':
                case 'decision':
                    // Forward live graph events to the canvas callback
                    onLiveGraphEvent?.(event);
                    // Also store as progress for the active step
                    // Use currentStep as fallback (BFS emits "incoming_trace"
                    // but the active investigation step is "backward_trace")
                    {
                        const rawStep = event.data.step as string | undefined;
                        const effectiveStep = (rawStep && INVESTIGATION_KEYS.includes(rawStep as InvestigationKey)
                            ? rawStep
                            : currentStep) as InvestigationKey | undefined;
                        if (effectiveStep && INVESTIGATION_KEYS.includes(effectiveStep)) {
                            setSections(prev => ({
                                ...prev,
                                [effectiveStep]: {
                                    ...prev[effectiveStep],
                                    progress: [...(prev[effectiveStep]?.progress || []), event],
                                },
                            }));
                        }
                    }
                    break;

                case 'phase':
                case 'hop': {
                    // Per-hop and phase updates from trace steps — store as progress
                    // These events may lack "step" — use currentStep as fallback
                    const traceStep = (event.data.step || currentStep) as InvestigationKey | undefined;
                    if (traceStep && INVESTIGATION_KEYS.includes(traceStep)) {
                        setSections(prev => ({
                            ...prev,
                            [traceStep]: {
                                ...prev[traceStep],
                                progress: [...(prev[traceStep]?.progress || []), { type: event.type, data: event.data }],
                            },
                        }));
                    }
                    break;
                }

                case 'complete': {
                    streamCompleted = true; // B1 cutoff: drop any further graph events
                    recordPipeline('sse', 'investigation_complete', {}, {
                        steps_completed: event.data.steps_completed,
                        total_duration_s: event.data.total_duration_s,
                        chain, address,
                    }, `${event.data.steps_completed ?? '?'} steps in ${event.data.total_duration_s || '?'}s`);
                    // Backend steps are done (some may be missing or degraded: see the
                    // coverage block below) — now run signal_assessment on frontend.
                    // Stubs for steps that did not finish are kept out of the results so
                    // a missing step stays absent for every consumer.
                    const allResults = event.data.all_results
                        ? stripCoverageStubs(event.data.all_results)
                        : collectedResults;
                    const runCoverage = normalizeCoverage(event.data);
                    if (runCoverage) {
                        setCoverage(runCoverage);
                        collectedResults._coverage = runCoverage;
                        // A step the backend reports missing or degraded must not look loaded,
                        // even if its step_result event was not seen.
                        for (const entry of runCoverage.missing_steps) {
                            const k = entry.step as InvestigationKey;
                            if (!INVESTIGATION_KEYS.includes(k)) continue;
                            const why = plainStepReason('skipped', entry.reason);
                            unfinishedSteps.set(k, why);
                            setSections(prev => (prev[k] && (prev[k].status === 'idle' || prev[k].status === 'loading')
                                ? { ...prev, [k]: { status: 'error', error: why, health: 'degraded', degradedReason: why } }
                                : prev));
                        }
                        for (const entry of runCoverage.degraded_steps) {
                            const k = entry.step as InvestigationKey;
                            const why = plainStepReason(entry.status, entry.reason);
                            if (!INVESTIGATION_KEYS.includes(k)) {
                                degradedSteps.set(k, why);
                                continue;
                            }
                            if (entry.status === 'timeout' || entry.status === 'error') {
                                if (!unfinishedSteps.has(k) && !failedSteps.has(k)) unfinishedSteps.set(k, why);
                                continue;
                            }
                            degradedSteps.set(k, why);
                            setSections(prev => (prev[k] && prev[k].status === 'loaded' && prev[k].health !== 'degraded'
                                ? { ...prev, [k]: { ...prev[k], health: 'degraded', degradedReason: why } }
                                : prev));
                        }
                    }
                    // Merge into collectedResults for signal_assessment
                    Object.assign(collectedResults, allResults);
                    lastStreamResultsRef.current = { ...allResults };
                    const promotion = event.data.spine_promotion;
                    setSpinePromotion(promotion && typeof promotion === 'object' && promotion.status ? promotion : null);

                    // Surface circulation / pass-through findings (cycle_detector
                    // runs as the backend 'circulation' step; it's in all_results
                    // but is NOT an INVESTIGATION_KEYS section, so expose it
                    // separately for the "Circulation & Pass-Through" UI).
                    const circ = allResults.circulation;
                    if (circ && Array.isArray(circ.findings)) {
                        setCirculation({ findings: circ.findings, summary: circ.summary || {} });
                    }

                    // Asset-recovery bundle: orchestrator post-trace passes land
                    // as _-prefixed keys in all_results (not INVESTIGATION_KEYS
                    // sections). Surface them for the report's Seizure Matrix,
                    // Fund-Flow Reconciliation, and Flow Diagram sections.
                    const ar: AssetRecoveryResult = {
                        seizure_matrix: allResults._seizure_matrix,
                        value_reconciliation: allResults._value_reconciliation,
                        flow_diagrams: allResults._flow_diagrams,
                        funding_sources: allResults._funding_sources,
                        bridge_settlements: allResults._bridge_settlements,
                        recheck_gates: allResults._recheck_gates,
                    };
                    if (ar.seizure_matrix || ar.value_reconciliation || ar.flow_diagrams
                        || ar.funding_sources || ar.bridge_settlements || ar.recheck_gates) {
                        setAssetRecovery(ar);
                    }

                    // Coverage rides on signal_assessment data so a saved run keeps it.
                    // V3-H4: Store provider health from orchestrator diagnostics
                    if (event.data.provider_health) {
                        collectedResults._provider_health = event.data.provider_health;
                    }

                    // Store transaction summary for report
                    if (event.data.transaction_summary) {
                        collectedResults._transaction_summary = event.data.transaction_summary;
                    }

                    // Run signal_assessment (frontend step 8) using the accumulated data
                    // Wrap with 45s timeout to prevent isComplete from never being set
                    markSection('signal_assessment', { status: 'loading' });
                    const fetchers = buildFetchers(chain, address, backwardHops, exposureHops, forwardHops, collectedResults,
                        undefined, undefined, caseLink.caseId ?? workbenchCaseId());
                    const signalWithTimeout = Promise.race([
                        fetchers.signal_assessment(),
                        new Promise((_, reject) => setTimeout(() => reject(new Error('Signal assessment timed out (45s)')), 45_000)),
                    ]);
                    signalWithTimeout
                        .then((data: any) => {
                            if (cancelledRef.current || runIdRef.current !== id) return;
                            const degraded = detectDegraded(data);
                            markSection('signal_assessment', {
                                status: 'loaded',
                                health: degraded.degraded ? 'degraded' : 'ok',
                                degradedReason: degraded.reason,
                                data,
                                loadedAt: new Date().toISOString(),
                            });
                            if (degraded.degraded) {
                                degradedSteps.set('signal_assessment', degraded.reason || 'partial data');
                            }
                        })
                        .catch((err: any) => {
                            if (cancelledRef.current || runIdRef.current !== id) return;
                            markSection('signal_assessment', {
                                status: 'error',
                                error: err?.message || 'Signal assessment failed',
                            });
                            failedSteps.set('signal_assessment', err?.message || 'failed');
                        })
                        .finally(() => {
                            if (runIdRef.current === id) {
                                setIsRunning(false);
                                setIsComplete(true);
                                // Summary toast: surface partial-data and failed
                                // steps in one place so the user knows when
                                // a "completed" investigation is actually
                                // incomplete. Counts > sample names keep the
                                // copy concise; the per-section detail is
                                // already visible inside the AutoInvestigate
                                // card.
                                // Genuine step failures (status="error") are the
                                // only thing that warrants an alarming toast.
                                if (failedSteps.size > 0) {
                                    const sample = Array.from(failedSteps.keys()).slice(0, 2)
                                        .map(k => _STEP_LABELS[k] || k);
                                    const more = failedSteps.size - sample.length;
                                    toast.error(
                                        `${failedSteps.size} step${failedSteps.size === 1 ? '' : 's'} failed (${sample.join(', ')}${more > 0 ? `, +${more}` : ''}). See the AutoInvestigate card for detail.`,
                                        { duration: 8000 },
                                    );
                                }
                                // Partial-data / budget-timeout sections are
                                // EXPECTED on large wallets — the step returned
                                // what it computed in its time budget, not an
                                // error. Surface them quietly so testers stop
                                // reading a normal big-wallet run as "broken".
                                if (unfinishedSteps.size > 0) {
                                    const sample = Array.from(unfinishedSteps.keys()).slice(0, 2)
                                        .map(k => _STEP_LABELS[k] || coverageStepLabel(k));
                                    const more = unfinishedSteps.size - sample.length;
                                    toast.warning(
                                        `${unfinishedSteps.size} step${unfinishedSteps.size === 1 ? '' : 's'} did not finish (${sample.join(', ')}${more > 0 ? `, +${more}` : ''}). The report lists them under Coverage and Limitations.`,
                                        { duration: 8000 },
                                    );
                                }
                                if (degradedSteps.size > 0) {
                                    const sample = Array.from(degradedSteps.keys()).slice(0, 2)
                                        .map(k => _STEP_LABELS[k] || k);
                                    const more = degradedSteps.size - sample.length;
                                    toast.info(
                                        `${degradedSteps.size} section${degradedSteps.size === 1 ? '' : 's'} returned partial data (${sample.join(', ')}${more > 0 ? `, +${more}` : ''}) — analysis still usable.`,
                                        { duration: 5000 },
                                    );
                                }
                            }
                        });
                    break;
                }

                case 'error': {
                    // Fatal stream error — stop everything
                    const errorStep = event.data.step as InvestigationKey | undefined;
                    if (errorStep && INVESTIGATION_KEYS.includes(errorStep)) {
                        markSection(errorStep, {
                            status: 'error',
                            error: event.data.message || 'Investigation stream error',
                        });
                    }
                    setIsRunning(false);
                    setIsComplete(true);
                    break;
                }

                case 'connection_error': {
                    // SSE failed — fall back to legacy POST-per-section flow
                    if (!event.data.receivedEvent) {
                        // Connection never established — use legacy run()
                        console.warn('Investigation SSE failed, falling back to POST-per-section');
                        runKeys(chain, address, INVESTIGATION_KEYS, id, backwardHops, exposureHops, forwardHops).finally(() => {
                            if (runIdRef.current === id) {
                                setIsRunning(false);
                                setIsComplete(true);
                            }
                        });
                    } else {
                        // Connection was established but dropped mid-stream: stop with partial
                        // results, and say so. Every step that had not finished is marked
                        // failed and degraded; the investigator is told with a toast.
                        const lost = 'Connection to the investigation stream was lost before this step finished';
                        setSections(prev => {
                            const next = { ...prev };
                            for (const k of INVESTIGATION_KEYS) {
                                const st = next[k]?.status;
                                if (st === 'idle' || st === 'loading') {
                                    next[k] = { status: 'error', error: lost, health: 'degraded', degradedReason: lost };
                                }
                            }
                            return next;
                        });
                        setCoverage({
                            steps_completed: null,
                            missing_steps: [],
                            degraded_steps: [{ step: 'stream', status: 'error', reason: lost }],
                        });
                        toast.warning(
                            'Connection to the investigation was lost mid-run. The results shown are partial; steps that had not finished are marked as such. Re-run to complete them.',
                            { duration: 10000 },
                        );
                        setIsRunning(false);
                        setIsComplete(true);
                    }
                    break;
                }

                // heartbeat events are ignored — they just keep the connection alive
            }
        };

        const { close } = investigationAnalysisService.investigationStream(
            chain,
            address,
            { backwardHops, exposureHops, forwardHops, ...caseLink },
            onEvent,
        );
        activeCloseRef.current = close;
    }, [markSection, runKeys]);

    const refresh = useCallback((chain: string, address: string, key?: InvestigationKey, backwardHops: number = 4, exposureHops: number = 3, forwardHops: number = 4) => {
        const keys = key
            ? [key]
            : INVESTIGATION_KEYS.filter(k => {
                const section = getSectionSafe(sections, k);
                return section.status === 'error' || section.health === 'degraded';
            });
        if (keys.length === 0) return;

        cancelledRef.current = false;
        const id = ++runIdRef.current;
        setIsRunning(true);
        setIsComplete(false);

        runKeys(chain, address, keys, id, backwardHops, exposureHops, forwardHops).finally(() => {
            if (runIdRef.current === id) {
                setIsRunning(false);
                setIsComplete(true);
            }
        });
    }, [sections, runKeys, getSectionSafe]);

    const cancel = useCallback(() => {
        cancelledRef.current = true;
        // Close any active SSE connection (backward/forward trace stream)
        activeCloseRef.current?.();
        activeCloseRef.current = null;
        setIsRunning(false);
        setPrefetchStatus('idle');
        // Revert any still-loading sections back to idle
        setSections(prev => {
            const next = { ...prev };
            for (const k of INVESTIGATION_KEYS) {
                if (getSectionSafe(next, k).status === 'loading') next[k] = { status: 'idle' };
            }
            return next;
        });
    }, [getSectionSafe]);

    const reset = useCallback(() => {
        cancelledRef.current = true;
        runIdRef.current++;
        setIsRunning(false);
        setIsComplete(false);
        setPrefetchStatus('idle');
        setSections({ ...INITIAL_SECTIONS });
        setCirculation(null);
        setAssetRecovery(null);
        setSpinePromotion(null);
        lastStreamResultsRef.current = null;
        investigatedAddressRef.current = '';
        investigatedChainRef.current = '';
        setRestoredFromSession(null);
        useInvestigationResultsStore.getState().clear();
    }, []);

    const hydrate = useCallback((
        saved: Partial<Record<InvestigationKey, any>>,
        opts?: { address?: string; circulation?: CirculationResult | null; assetRecovery?: AssetRecoveryResult | null },
    ) => {
        cancelledRef.current = true;
        runIdRef.current++;
        setIsRunning(false);
        setSpinePromotion(null);
        setRestoredFromSession(null);
        lastStreamResultsRef.current = null;
        const hydrated = { ...INITIAL_SECTIONS } as InvestigationSections;
        let hasAny = false;
        for (const key of INVESTIGATION_KEYS) {
            if (saved[key] !== undefined && saved[key] !== null) {
                const raw = saved[key];
                const loadedAt = raw?._loadedAt || undefined;
                const health = raw?._health || undefined;
                const degradedReason = raw?._degradedReason || undefined;
                // Strip _loadedAt metadata from data to avoid polluting downstream consumers
                let cleanData = raw;
                if (raw && typeof raw === 'object' && '_loadedAt' in raw) {
                    if ('_value' in raw) {
                        // Non-object payload was wrapped by exportForSave
                        cleanData = raw._value;
                    } else {
                        const { _loadedAt: _, _health: __, _degradedReason: ___, ...rest } = raw;
                        cleanData = rest;
                    }
                }
                hydrated[key] = { status: 'loaded', data: cleanData, loadedAt, health, degradedReason };
                hasAny = true;
            }
        }
        setSections(hydrated);
        setIsComplete(hasAny);
        if (opts?.address) {
            investigatedAddressRef.current = opts.address;
        }
        if (opts && 'circulation' in opts) {
            setCirculation(opts.circulation ?? null);
        }
        if (opts && 'assetRecovery' in opts) {
            setAssetRecovery(opts.assetRecovery ?? null);
        }
    }, []);

    const exportForSave = useCallback((): Partial<Record<InvestigationKey, any>> | null => {
        const hasLoaded = INVESTIGATION_KEYS.some(k => getSectionSafe(sections, k).status === 'loaded');
        if (!hasLoaded) return null;
        const result: Partial<Record<InvestigationKey, any>> = {};
        for (const key of INVESTIGATION_KEYS) {
            const section = getSectionSafe(sections, key);
            if (section.status === 'loaded') {
                // Embed _loadedAt so hydrate() can restore data freshness timestamps
                const d = section.data;
                result[key] = section.loadedAt
                    ? {
                        ...(d && typeof d === 'object' ? d : { _value: d }),
                        _loadedAt: section.loadedAt,
                        _health: section.health,
                        _degradedReason: section.degradedReason,
                    }
                    : d;
            }
        }
        return result;
    }, [sections, getSectionSafe]);

    const exportResultsForPromotion = useCallback((): Record<string, any> | null => {
        // Stream runs keep the raw post-trace passes; a hydrated or per-section
        // run has only the asset-recovery bundle. Loaded sections win, so a
        // refreshed trace replaces the streamed one.
        const base: Record<string, any> = { ...(lastStreamResultsRef.current || {}) };
        if (!lastStreamResultsRef.current && assetRecovery) {
            if (assetRecovery.seizure_matrix) base._seizure_matrix = assetRecovery.seizure_matrix;
            if (assetRecovery.funding_sources) base._funding_sources = assetRecovery.funding_sources;
            if (assetRecovery.bridge_settlements) base._bridge_settlements = assetRecovery.bridge_settlements;
        }
        for (const key of INVESTIGATION_KEYS) {
            const section = sections[key];
            if (section?.status === 'loaded' && section.data && typeof section.data === 'object') base[key] = section.data;
        }
        return promotionResults(base);
    }, [sections, assetRecovery]);

    // -- Persist the results of the current run (sessionStorage) --------------
    // The hook state above is the working copy; this mirrors loaded sections into
    // useInvestigationResultsStore so they survive leaving /workbench and a reload.
    const writeNowRef = useRef<(() => void) | null>(null);
    const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const address = investigatedAddressRef.current;
        if (!address) { writeNowRef.current = null; return; }
        // Untouched restore: nothing new to write, and rewriting would refresh the saved time.
        if (restored
            && sections === restored.sections && circulation === restored.circulation
            && assetRecovery === restored.assetRecovery && coverage === restored.coverage
            && spinePromotion === restored.spinePromotion) { writeNowRef.current = null; return; }
        const loaded: Record<string, PersistedSection> = {};
        for (const key of INVESTIGATION_KEYS) {
            const sec = sections[key];
            if (sec?.status === 'loaded' && sec.data !== undefined) {
                loaded[key] = { data: sec.data, loadedAt: sec.loadedAt, health: sec.health, degradedReason: sec.degradedReason };
            }
        }
        if (Object.keys(loaded).length === 0) { writeNowRef.current = null; return; }
        const complete = isComplete && !isRunning;
        writeNowRef.current = () => {
            const entry = buildResultsEntry({
                chain: investigatedChainRef.current,
                address,
                canvasVersion: computeLinksMemoVersion(canvasAddressNodeIds(useGraphStore.getState().elements)),
                complete,
                sections: loaded,
                circulation,
                assetRecovery,
                coverage,
                spinePromotion,
            });
            if (entry) useInvestigationResultsStore.getState().save(entry);
        };
        if (complete) {
            if (writeTimerRef.current) { clearTimeout(writeTimerRef.current); writeTimerRef.current = null; }
            writeNowRef.current();
        } else if (!writeTimerRef.current) {
            // Throttle during a run: sections change many times a second.
            writeTimerRef.current = setTimeout(() => {
                writeTimerRef.current = null;
                writeNowRef.current?.();
            }, 1500);
        }
    }, [sections, circulation, assetRecovery, coverage, spinePromotion, isComplete, isRunning, restored]);
    // Leaving the page mid-run: keep whatever had finished.
    useEffect(() => () => {
        if (writeTimerRef.current) { clearTimeout(writeTimerRef.current); writeTimerRef.current = null; }
        writeNowRef.current?.();
    }, []);

    return {
        sections,
        restoredFromSession,
        circulation,
        assetRecovery,
        spinePromotion,
        coverage,
        exportResultsForPromotion,
        isRunning,
        isComplete,
        prefetchStatus,
        progress: getProgress(),
        investigatedAddress: investigatedAddressRef.current,
        run,
        runViaStream,
        refresh,
        cancel,
        reset,
        hydrate,
        exportForSave,
    };
}
